/**
 * BCE Product Overview Module
 *
 * Sectioned product-card grid with a responsive mobile accordion. The styling
 * and render logic live centrally in BCE; the editor only authors two JSON
 * blocks inside the container (same translatable split as branche-full):
 *
 *   <div class="bce_product-overview">
 *     <script type="application/json" data-product-meta>
 *       [ { "id": "incubators",
 *           "products": [ { "icon": "Icon-….svg", "link": "/de-de/…" } ] } ]
 *     </script>
 *     <script type="application/json" data-crosstree-i18n>
 *       [ { "title": "Inkubatoren / Brutschränke",
 *           "products": [ { "title": "Standard-Inkubatoren",
 *                           "description": "…" } ] } ]
 *     </script>
 *   </div>
 *
 * data-product-meta  = structure (icon + link), never translated.
 * data-crosstree-i18n = section title + product title/description, translated
 *                       by the cross-tree translator. Zipped by index.
 *
 * Icons resolve against data-icon-base (default the TYPO3 product-icons folder),
 * so new icons are uploaded there — no BCE deploy needed.
 *
 * @class BceProductOverview
 */

const DEFAULT_ICON_BASE = '/fileadmin/user_upload/Icons/product-icons';
let instanceCounter = 0;

class BceProductOverview {
    constructor() {
        this.instances = new WeakMap();
        this.autoInit = false;
    }

    init(selector = '.bce_product-overview') {
        document.querySelectorAll(selector).forEach((el) => {
            if (!this.instances.has(el)) {
                this.create(el);
            }
        });
    }

    /**
     * @param {HTMLElement} root - element with the bce_product-overview class
     * @returns {Object|null}
     */
    create(root) {
        if (this.instances.has(root)) {
            return this.instances.get(root);
        }

        const metaScript = root.querySelector('script[data-product-meta]');
        if (!metaScript) {
            console.warn('BCE product-overview: no data-product-meta found');
            return null;
        }

        const meta = this.parseJson(metaScript, 'meta');
        if (!Array.isArray(meta) || meta.length === 0) {
            console.warn('BCE product-overview: data-product-meta must be a non-empty array');
            return null;
        }

        const i18nScript = root.querySelector('[data-crosstree-i18n]');
        const i18n = i18nScript ? this.parseJson(i18nScript, 'i18n') || [] : [];

        const sections = this.mergeSections(meta, i18n);
        const iconBase = root.dataset.iconBase || DEFAULT_ICON_BASE;
        const gridId = `bce-product-overview-${(instanceCounter += 1)}`;

        // Remove the source scripts and build the grid container
        [metaScript, i18nScript].forEach((el) => el && el.remove());
        const grid = document.createElement('div');
        grid.className = 'product-overview';
        root.appendChild(grid);

        const instance = {
            root,
            grid,
            gridId,
            sections,
            iconBase,
            isMobile: window.innerWidth < 768,
            resizeHandler: null,
            resizeTimer: null,
            destroy: () => this.destroyInstance(root)
        };
        this.instances.set(root, instance);

        this.render(instance);
        this.setMinHeightRatio(instance);
        this.attachResize(instance);

        return instance;
    }

    /**
     * Zip structural meta with translatable i18n by section and product index.
     * @param {Array} meta
     * @param {Array} i18n
     * @returns {Array}
     */
    mergeSections(meta, i18n) {
        return meta.map((section, s) => {
            const t = i18n[s] || {};
            const tProducts = t.products || [];
            const products = (section.products || []).map((p, i) => {
                const tp = tProducts[i] || {};
                return {
                    icon: p.icon || '',
                    link: p.link || '',
                    title: tp.title || '',
                    description: tp.description || ''
                };
            });
            return { id: section.id || `section-${s}`, title: t.title || '', products };
        });
    }

    /**
     * Render desktop grid or mobile accordion into the container.
     * @param {Object} instance
     */
    render(instance) {
        const { grid, sections, iconBase, isMobile, gridId } = instance;
        let html = '';

        sections.forEach((section) => {
            if (isMobile) {
                const collapseId = `${gridId}-collapse-${section.id}`;
                html += `
                    <div class="accordion-item section-title">
                        <h2 class="accordion-header">
                            <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false" aria-controls="${collapseId}">
                                ${this.escape(section.title)}
                                <span class="accordion-icon"></span>
                            </button>
                        </h2>
                    </div>
                    <div id="${collapseId}" class="accordion-collapse collapse" data-bs-parent="#${gridId}">
                        <div class="accordion-body">
                            ${section.products.map((p) => this.card(p, iconBase)).join('')}
                        </div>
                    </div>`;
            } else {
                html += `<h2 class="section-title">${this.escape(section.title)}</h2>`;
                html += section.products.map((p) => this.card(p, iconBase)).join('');
            }
        });

        grid.innerHTML = html;

        if (isMobile) {
            grid.classList.add('accordion');
            grid.setAttribute('id', gridId);
        } else {
            grid.classList.remove('accordion');
            grid.setAttribute('id', gridId);
        }

        // External links open in a new tab, internal ones stay in the same tab
        grid.querySelectorAll('a.product-card').forEach((a) => this.applyLinkTarget(a));
    }

    /**
     * Single product-card markup.
     * @param {Object} p - { icon, link, title, description }
     * @param {string} iconBase
     * @returns {string}
     */
    card(p, iconBase) {
        const src = `${iconBase}/${p.icon}`;
        const desc = p.description
            ? `<p class="product-description">${this.escape(p.description)}</p>`
            : '';
        return `
            <a href="${this.escape(p.link)}" class="product-card">
                <div class="product-icon"><img src="${this.escape(src)}" alt="${this.escape(p.title)}"></div>
                <div class="product-content">
                    <h3 class="product-title">${this.escape(p.title)}</h3>
                    ${desc}
                </div>
            </a>`;
    }

    /**
     * Desktop cards are square: min-height follows the rendered width.
     * @param {Object} instance
     */
    setMinHeightRatio(instance) {
        if (instance.isMobile) return;
        instance.grid.querySelectorAll('.product-card').forEach((card) => {
            card.style.minHeight = `${card.offsetWidth}px`;
        });
    }

    /**
     * Re-render on the mobile/desktop breakpoint crossing; re-square on resize.
     * @param {Object} instance
     */
    attachResize(instance) {
        instance.resizeHandler = () => {
            const wasMobile = instance.isMobile;
            instance.isMobile = window.innerWidth < 768;

            if (wasMobile !== instance.isMobile) {
                this.render(instance);
            }

            clearTimeout(instance.resizeTimer);
            instance.resizeTimer = setTimeout(() => {
                if (window.innerWidth >= 768) {
                    this.setMinHeightRatio(instance);
                } else {
                    instance.grid.querySelectorAll('.product-card').forEach((card) => {
                        card.style.minHeight = '';
                    });
                }
            }, 250);
        };
        window.addEventListener('resize', instance.resizeHandler);
    }

    /**
     * @param {HTMLElement} root
     */
    destroyInstance(root) {
        const instance = this.instances.get(root);
        if (!instance) return;
        if (instance.resizeHandler) window.removeEventListener('resize', instance.resizeHandler);
        clearTimeout(instance.resizeTimer);
        this.instances.delete(root);
    }

    /**
     * Open external links in a new tab, internal ones in the same tab.
     * @param {HTMLAnchorElement} a
     */
    applyLinkTarget(a) {
        const href = a.getAttribute('href') || '';
        let external = false;
        try {
            external = new URL(href, window.location.href).host !== window.location.host;
        } catch (e) {
            external = false;
        }
        if (external) {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
        } else {
            a.removeAttribute('target');
        }
    }

    /**
     * @param {HTMLElement} script
     * @param {string} label
     * @returns {*}
     */
    parseJson(script, label) {
        try {
            return JSON.parse(script.textContent.trim());
        } catch (error) {
            console.warn(`BCE product-overview: invalid ${label} JSON`, error);
            return null;
        }
    }

    /**
     * @param {string} s
     * @returns {string}
     */
    escape(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    getInstance(root) {
        return this.instances.get(root) || null;
    }
}

export default new BceProductOverview();
